import 'react-loading-skeleton/dist/skeleton.css';

import {
  GENERAL_ENQUIRIES,
  MEDIA_PARTNERSHIP_EMAIL,
  SPEAKER_EMAIL,
  SPONSOR_EMAIL,
} from '@utils/constants';
import { Trans, useTranslation } from 'react-i18next';
import {
  AppLogo,
  CompanyLogo,
  ContactUs,
  DesktopOnlyColumn,
  DetailTitle,
  EventDetailsDescription,
  EventDetailsSection,
  FooterRoot,
  FooterWrapper,
  LinkElement,
  LinkElementRoot,
  MenuFooterRoot,
  MobileOnlyColumn,
  TextLink,
} from './styles';

import weLogo from '@assets/images/weCONECT_col_RGB.svg';
import Button from '@components/Button';
import { Column } from '@components/LinearLayout';
import {
  adminEventsRouteFormat,
  adminPortfoliosRouteFormat,
} from '@constants/paths';
import { getLanguage } from '@store/slices/configSlice';
import { getEventDetails } from '@store/slices/eventSlice';
import {
  ACCOUNT_ID,
  usePublicCoLocatedEventsByEventId,
  usePublicPortfolioByAccount,
  PORTFOLIO_GENERAL_EMAILS,
} from '@utils/apiHooks';
import { getImageSrc } from '@utils/fn';
import { useMobileWidth, useTabletWidth } from '@utils/hooks';
import { eventTimeFormat } from '@utils/time';
import { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import SaveTheDate from '@composites/InboundButton/components/SaveTheDate';
import { MLSID } from '@utils/pageBuilderHelper';
import { useNavigate } from 'react-router-dom';

export function FooterMenu() {
  const { t } = useTranslation();
  const language = useSelector(getLanguage);
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const navigate = useNavigate();
  const isMobile = useMobileWidth();

  const { data, isLoading } = usePublicCoLocatedEventsByEventId({
    eventId: sluggedEvent?.id,
    enabled: sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated,
  });

  const { data: portfolios } = usePublicPortfolioByAccount({
    accountId: ACCOUNT_ID,
  });

  // --- START robust parse ---
  const parsePortfolioEmailsLoose = (raw?: string): Record<string, string> => {
    const v = (raw ?? '').trim();
    if (!v) return {};
    try {
      return JSON.parse(v) as Record<string, string>;
    } catch {}

    const normalized = v
      .replace(/^[^{]*{/, '{')
      .replace(/}[^}]*$/, '}')
      .replace(/'/g, '"')
      .replace(/([{,]\s*)([A-Za-z0-9_-]+)\s*:/g, '$1"$2":')
      .replace(/:\s*(?!")([A-Za-z0-9_.@+-]+)\s*(?=[,}])/g, ':"$1"')
      .replace(/,\s*}/g, '}');

    try {
      return JSON.parse(normalized) as Record<string, string>;
    } catch {}

    const body = v.slice(v.indexOf('{') + 1, v.lastIndexOf('}'));
    const out: Record<string, string> = {};
    body
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .forEach(part => {
        const idx = part.indexOf(':');
        if (idx === -1) return;
        const k = part
          .slice(0, idx)
          .trim()
          .replace(/^['"]|['"]$/g, '')
          .toLowerCase();
        const val = part
          .slice(idx + 1)
          .trim()
          .replace(/^['"]|['"]$/g, '');
        if (k) out[k] = val;
      });
    return out;
  };
  // --- END robust parse ---

  const rawEmails = (PORTFOLIO_GENERAL_EMAILS || '').trim();
  const portfolioEmailsMap = useMemo(
    () => parsePortfolioEmailsLoose(rawEmails),
    [rawEmails]
  );

  const portfolioSlug = (sluggedEvent?.portfolio?.slug || '').toLowerCase();
  const generalEnquiriesRecipient =
    portfolioEmailsMap[portfolioSlug] ?? GENERAL_ENQUIRIES;

  const portfolioName = sluggedEvent?.portfolio?.name;

  const location = useMemo(
    () =>
      [
        sluggedEvent?.location?.name,
        sluggedEvent?.location?.marker.street,
        [
          sluggedEvent?.location?.marker?.postalCode,
          sluggedEvent?.location?.marker?.city,
        ]
          ?.filter(e => e)
          ?.join(' '),
      ].filter(e => e) ?? [],
    [
      sluggedEvent?.location?.marker?.city,
      sluggedEvent?.location?.marker?.postalCode,
      sluggedEvent?.location?.marker.street,
      sluggedEvent?.location?.name,
    ]
  );

  const eventName = sluggedEvent?.name ?? '';

  const isWebinar = !!sluggedEvent?.webinar;

  const speakerSubject = t('apply-as-a-speaker-request-subject', { eventName });
  const speakerBody = t('apply-as-a-speaker-request-body', { eventName });
  const sponsorSubject = t('sponsorMailto.subject', { eventName });
  const sponsorBody = t('sponsorMailto.body', { eventName });

  const speakerEmailHref = isWebinar
    ? `mailto:${SPEAKER_EMAIL}`
    : `mailto:${SPEAKER_EMAIL}?subject=${encodeURIComponent(speakerSubject)}&body=${encodeURIComponent(speakerBody)}`;

  const sponsorMailHref = isWebinar
    ? `mailto:${SPONSOR_EMAIL}`
    : `mailto:${SPONSOR_EMAIL}?subject=${encodeURIComponent(sponsorSubject)}&body=${encodeURIComponent(sponsorBody)}`;

  const mediaSubject = t('media-partnership-email.subject', { eventName });
  const mediaBody = t('media-partnership-email.body', { eventName });

  const mediaMailHref = isWebinar
    ? `mailto:${MEDIA_PARTNERSHIP_EMAIL}`
    : `mailto:${MEDIA_PARTNERSHIP_EMAIL}?subject=${encodeURIComponent(mediaSubject)}&body=${encodeURIComponent(mediaBody)}`;

  const generalEnquiriesMailHref = `mailto:${generalEnquiriesRecipient}`;
  const eventElements = useMemo(
    () => [
      { title: t('sponsoring'), link: sponsorMailHref },
      { title: t('to-speaker'), link: speakerEmailHref },
      {
        title: t('media-partnership'),
        link: mediaMailHref,
      },
      { title: t('general-enquiries'), link: generalEnquiriesMailHref },
    ],
    [
      t,
      sponsorMailHref,
      speakerEmailHref,
      mediaMailHref,
      generalEnquiriesMailHref,
    ]
  );

  const getLogoSrc = () => {
    const eventLogo = sluggedEvent?.design?.eventLogoPosSvg;
    const portfolioLogo = portfolios?.data?.find(
      p => p?.id === sluggedEvent?.portfolio?.id
    )?.design?.logoPosImage;

    if (portfolioLogo) {
      return getImageSrc(portfolioLogo);
    } else if (eventLogo) {
      return getImageSrc(eventLogo);
    } else {
      return weLogo;
    }
  };
  const getLogoAlt = () => {
    if (sluggedEvent?.portfolio?.name) {
      return sluggedEvent?.portfolio?.name;
    } else {
      return sluggedEvent?.name?.length ? sluggedEvent?.name : 'WeConect Logo';
    }
  };

  return (
    <MenuFooterRoot $backgroundColor={theme.color.lightGrey}>
      <FooterRoot>
        <FooterWrapper>
          <EventDetailsSection>
            {sluggedEvent?.portfolio?.slug ? (
              <a
                href={adminPortfoliosRouteFormat(
                  sluggedEvent?.portfolio?.slug,
                  ''
                )}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={sluggedEvent?.portfolio?.name || 'Portfolio'}
                style={{ display: 'inline-block' }}
              >
                <CompanyLogo
                  src={getLogoSrc()}
                  alt={getLogoAlt()}
                  loading="lazy"
                />
              </a>
            ) : (
              <CompanyLogo
                src={getLogoSrc()}
                alt={getLogoAlt()}
                loading="lazy"
              />
            )}

            {portfolioName && (
              <EventDetailsDescription>
                {sluggedEvent?.name}
                <Trans
                  i18nKey="event-details-description"
                  portfolioName={sluggedEvent?.portfolio?.name}
                >
                  is part of the {{ portfolioName }} Event Series. More
                  information can be found
                  <TextLink
                    href={adminPortfoliosRouteFormat(
                      sluggedEvent?.portfolio?.slug,
                      ''
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    $color={sluggedEvent?.design?.themeColor}
                  >
                    here
                  </TextLink>
                  .
                </Trans>
              </EventDetailsDescription>
            )}
          </EventDetailsSection>
          <EventDetailsSection>
            <Column>
              <DetailTitle>{t('date')}</DetailTitle>
              {isLoading ? (
                <Skeleton
                  count={1}
                  width="20rem"
                  baseColor="#dedede"
                  enableAnimation
                />
              ) : (
                <EventDetailsDescription>
                  <time dateTime={sluggedEvent?.start_date}>
                    {eventTimeFormat(
                      sluggedEvent?.start_date,
                      sluggedEvent?.end_date,
                      language
                    )}
                  </time>
                </EventDetailsDescription>
              )}
            </Column>

            {location?.filter(e => e)?.length > 0 && (
              <Column>
                <DetailTitle>{t('location')}</DetailTitle>

                {isLoading ? (
                  <Skeleton
                    count={3}
                    width="15rem"
                    enableAnimation
                    baseColor="#dedede"
                    style={{ marginTop: '0.8rem' }}
                  />
                ) : (
                  <Column $gap="0.3rem">
                    {location?.map(item => (
                      <EventDetailsDescription key={item}>
                        {item}
                      </EventDetailsDescription>
                    ))}
                  </Column>
                )}
              </Column>
            )}
          </EventDetailsSection>

          <EventDetailsSection>
            <DesktopOnlyColumn>
              <ContactUs href="mailto:contact@we-conect.com">
                <DetailTitle $color={sluggedEvent?.design?.themeColor}>
                  {t('contact-us')}
                </DetailTitle>
                <Column $gap="0.5rem">
                  <LinkElementRoot to="tel:+493052107030">
                    <LinkElement $color={sluggedEvent?.design?.themeColor}>
                      <span>{'+49 (0)30 52 10 70 3 0'}</span>
                    </LinkElement>
                  </LinkElementRoot>
                </Column>
              </ContactUs>

              <DetailTitle>{t('email')}</DetailTitle>
              <Column $gap="0.5rem">
                {eventElements?.map(item => (
                  <LinkElementRoot key={item.title} to={item.link}>
                    <LinkElement $color={sluggedEvent?.design?.themeColor}>
                      <span>&gt;</span> <span>{item.title}</span>
                    </LinkElement>
                  </LinkElementRoot>
                ))}
              </Column>
            </DesktopOnlyColumn>

            <MobileOnlyColumn>
              <DetailTitle $color={sluggedEvent?.design?.themeColor}>
                {t('phone')}
              </DetailTitle>
              <Column $gap="0.5rem" margin="0 0 0 12rem">
                <LinkElementRoot to="tel:+493052107030">
                  <LinkElement $color={sluggedEvent?.design?.themeColor}>
                    <span>{'+49 (0)30 52 10 70 3 0'}</span>
                  </LinkElement>
                </LinkElementRoot>
              </Column>

              <DetailTitle>{t('email')}</DetailTitle>
              <Column $gap="0.5rem" margin="0 0 0 12rem">
                {eventElements?.map(item => (
                  <LinkElementRoot key={item.title} to={item.link}>
                    <LinkElement $color={sluggedEvent?.design?.themeColor}>
                      <span>&gt;</span> <span>{item.title}</span>
                    </LinkElement>
                  </LinkElementRoot>
                ))}
              </Column>
            </MobileOnlyColumn>

            {sluggedEvent?.colocated && (
              <SaveTheDate
                label={t('save-the-date')}
                buttonType={MLSID.SAVE_THE_DATE}
                width={isMobile ? 'auto' : '100%'}
                padding={isMobile ? '1.5rem 4.5rem' : '1.1rem 0'}
              />
            )}
          </EventDetailsSection>

          {sluggedEvent?.colocated ? (
            <EventDetailsSection>
              <EventDetailsDescription>
                {t('this-event-is-co-located-with', {
                  eventName: sluggedEvent?.name,
                })}
              </EventDetailsDescription>
              {isLoading ? (
                <Column $gap="1.5rem">
                  <Skeleton
                    count={1}
                    enableAnimation
                    width="15rem"
                    height="10rem"
                    baseColor="#dedede"
                  />
                  <Skeleton
                    count={1}
                    enableAnimation
                    width="15rem"
                    height="10rem"
                    baseColor="#dedede"
                  />
                </Column>
              ) : (
                <Column $gap="2rem">
                  {data
                    ?.filter(e => e?.id !== sluggedEvent?.id)
                    ?.map(event => (
                      <CompanyLogo
                        key={event?.id}
                        src={getImageSrc(event?.images.logoTransparent)}
                        onClick={() => {
                          navigate(adminEventsRouteFormat(event?.slug, ''));
                        }}
                      />
                    ))}
                </Column>
              )}
            </EventDetailsSection>
          ) : (
            <EventDetailsSection>
              <EventDetailsDescription>
                {t('add-event-to-calendar-text')}
              </EventDetailsDescription>

              <SaveTheDate
                label={t('save-the-date')}
                buttonType={MLSID.SAVE_THE_DATE}
                width={isMobile ? 'auto' : '100%'}
                padding={isMobile ? '1.5rem  4.5rem' : '1.1rem 0'}
              />
            </EventDetailsSection>
          )}
        </FooterWrapper>
      </FooterRoot>
    </MenuFooterRoot>
  );
}

export default FooterMenu;
